import { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UtensilsCrossed, Store, BarChart3, Upload, Download, Plus, Pencil, Power, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

const DIETARY = ['high_protein', 'low_calorie', 'low_carb', 'keto', 'vegetarian', 'vegan', 'gluten_free', 'dairy_free', 'pescatarian', 'balanced', 'muscle_gain', 'fat_loss', 'maintenance'];
const ALLERGY = ['dairy', 'gluten', 'nuts', 'peanuts', 'soy', 'eggs', 'fish', 'shellfish', 'sesame'];
const CATEGORIES = ['breakfast', 'lunch', 'dinner', 'snack', 'high_protein', 'post_workout'];
const GOALS = ['muscle_gain', 'fat_loss', 'maintenance'];

const emptyProvider = { name: '', description: '', website_url: '', affiliate_url: '', promo_text: '', coupon_code: '', delivery_regions: '', min_order_info: '', subscription_required: false, price_range: '', avg_meal_price: '', is_featured: false, display_priority: 0 };
const emptyMeal = { provider_id: '', name: '', description: '', image_url: '', calories: '', protein: '', carbs: '', fats: '', fiber: '', sodium: '', serving_size: '', price: '', dietary_tags: [], allergy_tags: [], protein_source: '', category: '', goal_tags: [], affiliate_url: '', is_featured: false };

const Stat = ({ label, value, sub }) => (
  <Card className="glass p-4 border border-stone-800">
    <p className="text-stone-400 text-xs uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-stone-500 text-xs">{sub}</p>}
  </Card>
);

const TagPicker = ({ options, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map(t => (
      <button key={t} type="button"
        onClick={() => onChange(value.includes(t) ? value.filter(x => x !== t) : [...value, t])}
        className={`text-xs px-2 py-0.5 rounded-full border capitalize ${value.includes(t) ? 'border-blood-600 bg-blood-950/40 text-blood-400' : 'border-stone-700 text-stone-500'}`}
        data-testid={`${testPrefix}-${t}`}>
        {t.replace(/_/g, ' ')}
      </button>
    ))}
  </div>
);

const AdminMeals = () => {
  const [section, setSection] = useState('analytics'); // analytics | providers | meals | csv
  const [analytics, setAnalytics] = useState(null);
  const [days, setDays] = useState(30);
  const [providers, setProviders] = useState([]);
  const [meals, setMeals] = useState([]);
  const [mealFilter, setMealFilter] = useState('');
  const [providerForm, setProviderForm] = useState(null); // null | {...} (with id = editing)
  const [mealForm, setMealForm] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, p, m] = await Promise.all([
        axios.get(`${API}/meals/admin/analytics?days=${days}`),
        axios.get(`${API}/meals/admin/providers`),
        axios.get(`${API}/meals/admin/meals`),
      ]);
      setAnalytics(a.data);
      setProviders(p.data.providers || []);
      setMeals(m.data.meals || []);
    } catch (e) {
      toast.error('Failed to load meal marketplace data');
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const saveProvider = async () => {
    try {
      const body = { ...providerForm, avg_meal_price: providerForm.avg_meal_price ? parseFloat(providerForm.avg_meal_price) : null, display_priority: parseInt(providerForm.display_priority) || 0 };
      if (providerForm.id) {
        await axios.put(`${API}/meals/admin/providers/${providerForm.id}`, body);
        toast.success('Provider updated');
      } else {
        await axios.post(`${API}/meals/admin/providers`, body);
        toast.success('Provider created');
      }
      setProviderForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save provider');
    }
  };

  const toggleProvider = async (p) => {
    try {
      await axios.put(`${API}/meals/admin/providers/${p.id}`, { is_active: !p.is_active });
      toast.success(p.is_active ? 'Provider deactivated' : 'Provider activated');
      load();
    } catch (e) { toast.error('Failed to update provider'); }
  };

  const saveMeal = async () => {
    try {
      const num = (v) => (v === '' || v == null ? null : parseFloat(v));
      const body = {
        ...mealForm,
        calories: parseFloat(mealForm.calories), protein: parseFloat(mealForm.protein),
        carbs: parseFloat(mealForm.carbs), fats: parseFloat(mealForm.fats),
        fiber: num(mealForm.fiber), sodium: num(mealForm.sodium), price: num(mealForm.price),
        category: mealForm.category || null,
      };
      if (mealForm.id) {
        await axios.put(`${API}/meals/admin/meals/${mealForm.id}`, body);
        toast.success('Meal updated');
      } else {
        await axios.post(`${API}/meals/admin/meals`, body);
        toast.success('Meal created');
      }
      setMealForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save meal (check required nutrition numbers)');
    }
  };

  const toggleMeal = async (m) => {
    try {
      await axios.put(`${API}/meals/admin/meals/${m.id}`, { is_active: !m.is_active });
      toast.success(m.is_active ? 'Meal deactivated' : 'Meal activated');
      load();
    } catch (e) { toast.error('Failed to update meal'); }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const res = await axios.get(`${API}/meals/admin/${path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) { toast.error('Please select a .csv file'); return; }
    setImporting(true);
    try {
      const text = await file.text();
      const res = await axios.post(`${API}/meals/admin/import-csv`, { csv_content: text });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} meals (${res.data.failed} rows failed)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const filteredMeals = mealFilter ? meals.filter(m => m.provider_id === mealFilter) : meals;
  const providerName = (id) => providers.find(p => p.id === id)?.name || 'Unknown';

  return (
    <div className="space-y-6" data-testid="admin-meals-tab">
      <div className="flex flex-wrap gap-2">
        {[['analytics', 'Analytics', BarChart3], ['providers', `Providers (${providers.length})`, Store], ['meals', `Meals (${meals.length})`, UtensilsCrossed], ['csv', 'CSV Import/Export', Upload]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setSection(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm ${section === key ? 'border-blood-600 text-blood-400 bg-blood-950/30' : 'border-stone-700 text-stone-400'}`}
            data-testid={`admin-meals-section-${key}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {section === 'analytics' && analytics && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-stone-500 text-sm">Range:</span>
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 rounded border text-sm ${days === d ? 'border-blood-600 text-blood-400' : 'border-stone-700 text-stone-400'}`} data-testid={`analytics-days-${d}`}>{d}d</button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Recommendation Views" value={analytics.recommendation_views} />
            <Stat label="Total Link Clicks" value={analytics.total_clicks} sub="Outbound clicks — not purchases" />
            <Stat label="Click-Through Rate" value={`${analytics.click_through_rate}%`} sub="Clicks / views" />
            <Stat label="Saved Meals" value={analytics.saved_meals} />
            <Stat label="Meal Card Clicks" value={analytics.meal_clicks} />
            <Stat label="Provider Page Clicks" value={analytics.provider_clicks} />
            <Stat label="Goal vs Today Clicks" value={`${analytics.goal_mode_clicks} / ${analytics.today_mode_clicks}`} />
            <Stat label="Food Log Additions" value={analytics.food_log_additions} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="glass p-4 border border-stone-800">
              <h4 className="text-blood-400 font-bold text-sm uppercase tracking-wider mb-2">Most-Clicked Providers</h4>
              {analytics.top_providers.length === 0 ? <p className="text-stone-500 text-sm">No clicks yet.</p> :
                analytics.top_providers.map((p, i) => <p key={i} className="text-stone-300 text-sm flex justify-between"><span>{p.name}</span><b>{p.clicks}</b></p>)}
            </Card>
            <Card className="glass p-4 border border-stone-800">
              <h4 className="text-blood-400 font-bold text-sm uppercase tracking-wider mb-2">Most-Clicked Meals</h4>
              {analytics.top_meals.length === 0 ? <p className="text-stone-500 text-sm">No clicks yet.</p> :
                analytics.top_meals.map((m, i) => <p key={i} className="text-stone-300 text-sm flex justify-between"><span>{m.name}</span><b>{m.clicks}</b></p>)}
            </Card>
          </div>
        </div>
      )}

      {section === 'providers' && (
        <div className="space-y-3">
          <Button size="sm" onClick={() => setProviderForm({ ...emptyProvider })} className="bg-gradient-to-r from-blood-700 to-blood-900 text-white text-xs font-bold" data-testid="add-provider-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Provider
          </Button>
          {providers.map(p => (
            <Card key={p.id} className={`glass p-4 border ${p.is_active ? 'border-stone-800' : 'border-stone-800 opacity-50'}`} data-testid={`admin-provider-${p.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-white font-bold">{p.name} {p.is_featured && <span className="text-amber-400 text-xs">★ featured</span>} {p.is_sample && <span className="text-stone-600 text-xs">(sample)</span>}</p>
                  <p className="text-stone-500 text-xs">Avg ${p.avg_meal_price ?? '—'} • {p.subscription_required ? 'Subscription' : 'No subscription'} • Priority {p.display_priority} • {p.coupon_code || 'no coupon'}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setProviderForm({ ...emptyProvider, ...p, avg_meal_price: p.avg_meal_price ?? '' })} className="glass border-stone-700 text-stone-300 text-xs" data-testid={`edit-provider-${p.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleProvider(p)} className={`glass text-xs ${p.is_active ? 'border-blood-800 text-blood-400' : 'border-green-800 text-green-400'}`} data-testid={`toggle-provider-${p.id}`}>
                  <Power className="w-3 h-3 mr-1" /> {p.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {section === 'meals' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <Button size="sm" onClick={() => setMealForm({ ...emptyMeal, provider_id: providers[0]?.id || '' })} className="bg-gradient-to-r from-blood-700 to-blood-900 text-white text-xs font-bold" data-testid="add-meal-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Meal
            </Button>
            <select value={mealFilter} onChange={(e) => setMealFilter(e.target.value)} className="glass text-white px-3 py-1.5 rounded-lg border border-stone-700 text-sm" data-testid="admin-meal-provider-filter">
              <option value="">All providers</option>
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredMeals.map(m => (
              <Card key={m.id} className={`glass p-3 border flex gap-3 ${m.is_active ? 'border-stone-800' : 'border-stone-800 opacity-50'}`} data-testid={`admin-meal-${m.id}`}>
                {m.image_url && <img src={m.image_url} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{m.name}</p>
                  <p className="text-stone-500 text-xs">{providerName(m.provider_id)} • {m.calories} cal / {m.protein}p / {m.carbs}c / {m.fats}f {m.price != null && `• $${m.price}`}</p>
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => setMealForm({ ...emptyMeal, ...m, fiber: m.fiber ?? '', sodium: m.sodium ?? '', price: m.price ?? '', category: m.category || '' })} className="text-stone-400 text-xs hover:text-white" data-testid={`edit-meal-${m.id}`}>Edit</button>
                    <button onClick={() => toggleMeal(m)} className={`text-xs ${m.is_active ? 'text-blood-400' : 'text-green-400'}`} data-testid={`toggle-meal-${m.id}`}>{m.is_active ? 'Deactivate' : 'Activate'}</button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {section === 'csv' && (
        <div className="space-y-4 max-w-xl">
          <Card className="glass p-5 border border-stone-800">
            <h4 className="text-blood-400 font-bold text-sm uppercase tracking-wider mb-2">Import Meals from CSV</h4>
            <p className="text-stone-400 text-sm mb-3">Bulk-add meals. Provider names in the CSV must match existing providers exactly. Invalid rows are reported and skipped.</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadCsv('csv-template', 'meal_import_template.csv')} className="glass border-stone-700 text-stone-300 text-xs" data-testid="download-template-btn">
                <Download className="w-3 h-3 mr-1" /> Download Template
              </Button>
              <label className="cursor-pointer">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-md bg-gradient-to-r from-blood-700 to-blood-900 text-white text-xs font-bold ${importing ? 'opacity-50' : ''}`}>
                  <Upload className="w-3 h-3 mr-1" /> {importing ? 'Importing...' : 'Choose CSV & Import'}
                </span>
                <input type="file" accept=".csv" onChange={handleImportFile} className="hidden" disabled={importing} data-testid="csv-file-input" />
              </label>
              <Button size="sm" variant="outline" onClick={() => downloadCsv('export-csv', 'meal_catalog_export.csv')} className="glass border-stone-700 text-stone-300 text-xs" data-testid="export-csv-btn">
                <Download className="w-3 h-3 mr-1" /> Export All Meals
              </Button>
            </div>
            {importResult && (
              <div className="mt-4 text-sm" data-testid="import-result">
                <p className="text-green-400">Imported: {importResult.imported}</p>
                <p className={importResult.failed ? 'text-blood-400' : 'text-stone-500'}>Failed rows: {importResult.failed}</p>
                {importResult.errors?.slice(0, 10).map((e, i) => (
                  <p key={i} className="text-stone-500 text-xs mt-1">Row {e.row} ({e.name}): {e.errors.join('; ')}</p>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Provider form modal */}
      {providerForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <Card className="glass border border-stone-700 max-w-lg w-full my-8 p-5" data-testid="provider-form-modal">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-bold">{providerForm.id ? 'Edit Provider' : 'Add Provider'}</h3>
              <button onClick={() => setProviderForm(null)}><X className="w-4 h-4 text-stone-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[['name', 'Name *'], ['website_url', 'Website URL'], ['affiliate_url', 'Affiliate URL'], ['logo_url', 'Logo URL'], ['promo_text', 'Promo text'], ['coupon_code', 'Coupon code'], ['delivery_regions', 'Delivery regions'], ['min_order_info', 'Minimum order'], ['price_range', 'Price range'], ['avg_meal_price', 'Avg meal price ($)'], ['display_priority', 'Display priority']].map(([k, lbl]) => (
                <div key={k} className={['name', 'promo_text'].includes(k) ? 'col-span-2' : ''}>
                  <label className="text-stone-500 text-xs">{lbl}</label>
                  <Input value={providerForm[k] ?? ''} onChange={(e) => setProviderForm({ ...providerForm, [k]: e.target.value })} className="bg-stone-900 border-stone-700 text-white text-sm" data-testid={`provider-form-${k}`} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-stone-500 text-xs">Description</label>
                <textarea value={providerForm.description ?? ''} onChange={(e) => setProviderForm({ ...providerForm, description: e.target.value })} className="w-full bg-stone-900 border border-stone-700 text-white text-sm rounded-md p-2" rows={2} data-testid="provider-form-description" />
              </div>
              <label className="flex items-center gap-2 text-stone-300 text-sm">
                <input type="checkbox" checked={!!providerForm.subscription_required} onChange={(e) => setProviderForm({ ...providerForm, subscription_required: e.target.checked })} data-testid="provider-form-subscription" /> Subscription required
              </label>
              <label className="flex items-center gap-2 text-stone-300 text-sm">
                <input type="checkbox" checked={!!providerForm.is_featured} onChange={(e) => setProviderForm({ ...providerForm, is_featured: e.target.checked })} data-testid="provider-form-featured" /> Featured
              </label>
            </div>
            <Button onClick={saveProvider} disabled={!providerForm.name} className="w-full mt-4 bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold text-sm" data-testid="provider-form-save">
              {providerForm.id ? 'Save Changes' : 'Create Provider'}
            </Button>
          </Card>
        </div>
      )}

      {/* Meal form modal */}
      {mealForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <Card className="glass border border-stone-700 max-w-xl w-full my-8 p-5" data-testid="meal-form-modal">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-bold">{mealForm.id ? 'Edit Meal' : 'Add Meal'}</h3>
              <button onClick={() => setMealForm(null)}><X className="w-4 h-4 text-stone-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className="text-stone-500 text-xs">Provider *</label>
                <select value={mealForm.provider_id} onChange={(e) => setMealForm({ ...mealForm, provider_id: e.target.value })} className="w-full glass text-white px-3 py-2 rounded-lg border border-stone-700 text-sm" data-testid="meal-form-provider">
                  {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {[['name', 'Name *', 2], ['image_url', 'Image URL', 2], ['calories', 'Calories *', 1], ['protein', 'Protein g *', 1], ['carbs', 'Carbs g *', 1], ['fats', 'Fat g *', 1], ['fiber', 'Fiber g', 1], ['sodium', 'Sodium mg', 1], ['serving_size', 'Serving size', 1], ['price', 'Price $', 1], ['protein_source', 'Protein source', 1], ['affiliate_url', 'Affiliate URL', 2]].map(([k, lbl, span]) => (
                <div key={k} className={span === 2 ? 'col-span-2' : ''}>
                  <label className="text-stone-500 text-xs">{lbl}</label>
                  <Input value={mealForm[k] ?? ''} onChange={(e) => setMealForm({ ...mealForm, [k]: e.target.value })} className="bg-stone-900 border-stone-700 text-white text-sm" data-testid={`meal-form-${k}`} />
                </div>
              ))}
              <div>
                <label className="text-stone-500 text-xs">Category</label>
                <select value={mealForm.category} onChange={(e) => setMealForm({ ...mealForm, category: e.target.value })} className="w-full glass text-white px-3 py-2 rounded-lg border border-stone-700 text-sm" data-testid="meal-form-category">
                  <option value="">None</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-stone-300 text-sm mt-4">
                <input type="checkbox" checked={!!mealForm.is_featured} onChange={(e) => setMealForm({ ...mealForm, is_featured: e.target.checked })} /> Featured
              </label>
              <div className="col-span-2">
                <label className="text-stone-500 text-xs">Description</label>
                <textarea value={mealForm.description ?? ''} onChange={(e) => setMealForm({ ...mealForm, description: e.target.value })} className="w-full bg-stone-900 border border-stone-700 text-white text-sm rounded-md p-2" rows={2} data-testid="meal-form-description" />
              </div>
              <div className="col-span-2">
                <label className="text-stone-500 text-xs block mb-1">Dietary tags</label>
                <TagPicker options={DIETARY} value={mealForm.dietary_tags || []} onChange={(v) => setMealForm({ ...mealForm, dietary_tags: v })} testPrefix="meal-diet" />
              </div>
              <div className="col-span-2">
                <label className="text-stone-500 text-xs block mb-1">Allergy tags (contains)</label>
                <TagPicker options={ALLERGY} value={mealForm.allergy_tags || []} onChange={(v) => setMealForm({ ...mealForm, allergy_tags: v })} testPrefix="meal-allergy" />
              </div>
              <div className="col-span-2">
                <label className="text-stone-500 text-xs block mb-1">Fitness-goal tags</label>
                <TagPicker options={GOALS} value={mealForm.goal_tags || []} onChange={(v) => setMealForm({ ...mealForm, goal_tags: v })} testPrefix="meal-goal" />
              </div>
            </div>
            <Button onClick={saveMeal} disabled={!mealForm.name || !mealForm.provider_id || mealForm.calories === ''} className="w-full mt-4 bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold text-sm" data-testid="meal-form-save">
              {mealForm.id ? 'Save Changes' : 'Create Meal'}
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminMeals;
